export class AudioService {
  private inputAudioCtx: AudioContext | null = null;
  private outputAudioCtx: AudioContext | null = null;
  private mediaStream: MediaStream | null = null;
  private inputSource: MediaStreamAudioSourceNode | null = null;
  private workletNode: AudioWorkletNode | null = null;
  private processor: ScriptProcessorNode | null = null;
  private analyser: AnalyserNode | null = null;

  private isRecording = false;
  private isMuted = false;
  private recordingGeneration = 0;
  private onAudioChunkCallback?: (base64Pcm: string) => void;

  // Playback state
  private isPlaying = false;
  private nextPlayTime = 0;
  private activeSourceNodes: Set<AudioBufferSourceNode> = new Set();
  private outputAnalyser: AnalyserNode | null = null;
  private onPlaybackStateChangeCallback?: (isPlaying: boolean) => void;
  private onRecordingEndedCallback?: () => void;

  public setOnPlaybackStateChange(cb: (isPlaying: boolean) => void): void {
    this.onPlaybackStateChangeCallback = cb;
  }

  public setOnRecordingEnded(cb: () => void): void {
    this.onRecordingEndedCallback = cb;
  }

  public isCurrentlyRecording(): boolean {
    return this.isRecording;
  }

  public async startRecording(onChunk: (base64Pcm: string) => void): Promise<void> {
    this.stopRecording();
    const gen = ++this.recordingGeneration;
    this.isMuted = false;
    this.onAudioChunkCallback = onChunk;

    const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContextClass || !navigator?.mediaDevices?.getUserMedia) {
      throw new Error('Microphone audio capture is not supported in this browser context');
    }

    // Pre-unlock 24kHz output AudioContext inside the user gesture so server audio playback is never blocked
    if (!this.outputAudioCtx || this.outputAudioCtx.state === 'closed') {
      this.outputAudioCtx = new AudioContextClass({ sampleRate: 24000 });
      this.outputAnalyser = this.outputAudioCtx.createAnalyser();
      this.outputAnalyser.fftSize = 64;
      this.outputAnalyser.connect(this.outputAudioCtx.destination);
      this.nextPlayTime = 0;
    }
    if (this.outputAudioCtx.state === 'suspended') {
      this.outputAudioCtx.resume().catch(() => {});
    }

    const ctx = new AudioContextClass({ sampleRate: 16000 });
    if (ctx.state === 'suspended') {
      ctx.resume().catch(() => {});
    }

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: 16000,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });
    } catch (err) {
      if (ctx.state !== 'closed') {
        try { await ctx.close(); } catch {}
      }
      throw err;
    }

    // If stopRecording() or another startRecording() ran while awaiting getUserMedia, clean up and exit safely
    if (gen !== this.recordingGeneration || (ctx.state as AudioContextState) === 'closed') {
      stream.getTracks().forEach((t) => t.stop());
      if ((ctx.state as AudioContextState) !== 'closed') {
        try { await ctx.close(); } catch {}
      }
      return;
    }

    if ((ctx.state as AudioContextState) === 'suspended') {
      try { await ctx.resume(); } catch {}
    }

    if (gen !== this.recordingGeneration || (ctx.state as AudioContextState) === 'closed') {
      stream.getTracks().forEach((t) => t.stop());
      if ((ctx.state as AudioContextState) !== 'closed') {
        try { await ctx.close(); } catch {}
      }
      return;
    }

    const source = ctx.createMediaStreamSource(stream);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 64;

    let activeProcessorNode: AudioNode | null = null;
    let legacyProcessor: ScriptProcessorNode | null = null;
    let workletNode: AudioWorkletNode | null = null;

    const AudioWorkletNodeClass =
      (window as any).AudioWorkletNode ||
      (typeof AudioWorkletNode !== 'undefined' ? AudioWorkletNode : undefined);

    if (ctx.audioWorklet && typeof ctx.audioWorklet.addModule === 'function' && AudioWorkletNodeClass) {
      try {
        await ctx.audioWorklet.addModule('/pcm-recorder-worklet.js');
        if (gen !== this.recordingGeneration || (ctx.state as AudioContextState) === 'closed') {
          stream.getTracks().forEach((t) => t.stop());
          if ((ctx.state as AudioContextState) !== 'closed') {
            try { await ctx.close(); } catch {}
          }
          return;
        }
        const node: AudioWorkletNode = new AudioWorkletNodeClass(ctx, 'pcm-recorder-processor');
        node.port.onmessage = (e: MessageEvent<Float32Array>) => {
          if (!this.isRecording || this.isMuted || gen !== this.recordingGeneration) return;
          const inputData = e.data;
          if (!inputData || inputData.length === 0) return;
          const pcm16 = this.floatTo16BitPCM(inputData);
          const base64 = this.arrayBufferToBase64(pcm16.buffer);
          if (this.onAudioChunkCallback) {
            this.onAudioChunkCallback(base64);
          }
        };
        workletNode = node;
        activeProcessorNode = node;
      } catch {
        // Fallback to ScriptProcessorNode if worklet module cannot be loaded
      }
    }

    if (!activeProcessorNode) {
      const processor = ctx.createScriptProcessor(2048, 1, 1);
      processor.onaudioprocess = (e) => {
        if (!this.isRecording || this.isMuted || gen !== this.recordingGeneration) return;

        const inputData = e.inputBuffer.getChannelData(0);
        const pcm16 = this.floatTo16BitPCM(inputData);
        const base64 = this.arrayBufferToBase64(pcm16.buffer);

        if (this.onAudioChunkCallback) {
          this.onAudioChunkCallback(base64);
        }
      };
      legacyProcessor = processor;
      activeProcessorNode = processor;
    }

    stream.getTracks().forEach((track) => {
      track.onended = () => {
        if (this.isRecording && gen === this.recordingGeneration) {
          this.stopRecording();
          if (this.onRecordingEndedCallback) {
            this.onRecordingEndedCallback();
          }
        }
      };
    });

    source.connect(analyser);
    analyser.connect(activeProcessorNode);
    activeProcessorNode.connect(ctx.destination);

    this.inputAudioCtx = ctx;
    this.mediaStream = stream;
    this.inputSource = source;
    this.analyser = analyser;
    this.workletNode = workletNode;
    this.processor = legacyProcessor;
    this.isRecording = true;
  }

  public setMuted(muted: boolean): void {
    this.isMuted = muted;
    if (this.mediaStream) {
      this.mediaStream.getAudioTracks().forEach((track) => {
        track.enabled = !muted;
      });
    }
  }

  public stopRecording(): void {
    ++this.recordingGeneration;
    this.isRecording = false;
    this.isMuted = false;
    this.onAudioChunkCallback = undefined;

    if (this.workletNode) {
      try {
        this.workletNode.port.onmessage = null;
        this.workletNode.disconnect();
      } catch {}
      this.workletNode = null;
    }
    if (this.processor) {
      try {
        this.processor.onaudioprocess = null;
        this.processor.disconnect();
      } catch {}
      this.processor = null;
    }
    if (this.analyser) {
      try { this.analyser.disconnect(); } catch {}
      this.analyser = null;
    }
    if (this.inputSource) {
      try { this.inputSource.disconnect(); } catch {}
      this.inputSource = null;
    }
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((t) => {
        t.onended = null;
        t.stop();
      });
      this.mediaStream = null;
    }
    if (this.inputAudioCtx) {
      const ctxToClose = this.inputAudioCtx;
      this.inputAudioCtx = null;
      if (ctxToClose.state !== 'closed') {
        try { ctxToClose.close().catch(() => {}); } catch {}
      }
    }
  }

  // --- Playback: 24kHz PCM from Gemini ---
  public queueServerAudio(base64Pcm: string): void {
    const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
    if (!this.outputAudioCtx || this.outputAudioCtx.state === 'closed') {
      this.outputAudioCtx = new AudioContextClass({ sampleRate: 24000 });
      this.outputAnalyser = this.outputAudioCtx.createAnalyser();
      this.outputAnalyser.fftSize = 64;
      this.outputAnalyser.connect(this.outputAudioCtx.destination);
      this.nextPlayTime = 0;
    }

    if (this.outputAudioCtx.state === 'suspended') {
      this.outputAudioCtx.resume().catch(() => {});
    }

    const float32 = this.base64PcmToFloat32(base64Pcm);
    if (float32.length === 0) return;

    if (!this.isPlaying) {
      this.isPlaying = true;
      if (this.onPlaybackStateChangeCallback) {
        this.onPlaybackStateChangeCallback(true);
      }
    }

    const buffer = this.outputAudioCtx.createBuffer(1, float32.length, 24000);
    buffer.getChannelData(0).set(float32);

    const source = this.outputAudioCtx.createBufferSource();
    source.buffer = buffer;

    if (this.outputAnalyser) {
      source.connect(this.outputAnalyser);
    } else {
      source.connect(this.outputAudioCtx.destination);
    }

    const now = Number(this.outputAudioCtx.currentTime) || 0;
    // Schedule on the audio thread's hardware clock with a 30ms lookahead so main-thread React reconciliation never causes gaps
    if (this.nextPlayTime < now + 0.005) {
      this.nextPlayTime = now + 0.03;
    }
    const startAt = this.nextPlayTime;
    const chunkDuration =
      typeof buffer.duration === 'number' && Number.isFinite(buffer.duration) && buffer.duration > 0
        ? buffer.duration
        : float32.length / 24000;
    this.nextPlayTime += chunkDuration;

    this.activeSourceNodes.add(source);

    source.onended = () => {
      try {
        if (typeof source.disconnect === 'function') {
          source.disconnect();
        }
      } catch {}
      this.activeSourceNodes.delete(source);
      if (this.activeSourceNodes.size === 0 && this.isPlaying) {
        this.isPlaying = false;
        this.nextPlayTime = 0;
        if (this.onPlaybackStateChangeCallback) {
          this.onPlaybackStateChangeCallback(false);
        }
      }
    };

    source.start(startAt);
  }

  public interruptPlayback(): void {
    this.nextPlayTime = 0;
    const nodes = Array.from(this.activeSourceNodes);
    this.activeSourceNodes.clear();
    for (const node of nodes) {
      try {
        node.onended = null;
        node.stop();
      } catch {}
      try {
        if (typeof node.disconnect === 'function') {
          node.disconnect();
        }
      } catch {}
    }
    this.isPlaying = false;
    if (this.onPlaybackStateChangeCallback) {
      this.onPlaybackStateChangeCallback(false);
    }
  }

  public getWaveformHeights(isAiSpeaking: boolean): number[] {
    // 13 frequency bars
    const barsCount = 13;
    const activeAnalyser = isAiSpeaking ? this.outputAnalyser : this.analyser;

    if (!activeAnalyser) {
      // Idle animation heights
      return [4, 6, 8, 12, 16, 14, 10, 8, 12, 16, 12, 8, 4];
    }

    const dataArray = new Uint8Array(activeAnalyser.frequencyBinCount);
    activeAnalyser.getByteFrequencyData(dataArray);

    const heights: number[] = [];
    const binSize = Math.max(1, Math.floor(dataArray.length / barsCount));

    for (let i = 0; i < barsCount; i++) {
      let sum = 0;
      for (let j = 0; j < binSize; j++) {
        sum += dataArray[i * binSize + j] || 0;
      }
      const avg = sum / binSize; // 0 to 255
      const height = Math.min(28, Math.max(4, Math.round((avg / 255) * 26 + 4)));
      heights.push(height);
    }

    return heights;
  }

  // --- Notification Chime Synthesizer ---
  public playNotificationChime(): void {
    try {
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      const ctx = new AudioContextClass();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = 'sine';
      osc.frequency.setValueAtTime(587.33, ctx.currentTime); // D5
      osc.frequency.exponentialRampToValueAtTime(880, ctx.currentTime + 0.15); // A5

      gain.gain.setValueAtTime(0.2, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.onended = () => {
        try { ctx.close().catch(() => {}); } catch {}
      };

      osc.start();
      osc.stop(ctx.currentTime + 0.4);
    } catch {}
  }

  // --- Conversion Helpers ---
  private floatTo16BitPCM(input: Float32Array): Int16Array {
    const output = new Int16Array(input.length);
    for (let i = 0; i < input.length; i++) {
      const s = Math.max(-1, Math.min(1, input[i]));
      output[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
    }
    return output;
  }

  private base64PcmToFloat32(base64: string): Float32Array {
    const binary = window.atob(base64);
    const len = binary.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    const sampleCount = Math.floor(bytes.byteLength / 2);
    const int16 = new Int16Array(bytes.buffer, 0, sampleCount);
    const float32 = new Float32Array(int16.length);
    for (let i = 0; i < int16.length; i++) {
      float32[i] = int16[i] / 32768.0;
    }
    return float32;
  }

  private arrayBufferToBase64(buffer: ArrayBuffer | ArrayBufferLike): string {
    let binary = '';
    const bytes = new Uint8Array(buffer as ArrayBuffer);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }
}

export const audioService = new AudioService();
